import guminyomasImg from "@/assets/blog/nyari-guminyomas-ellenorzes-hosegben.webp";
import klimaSzervizImg from "@/assets/blog/auto-klimaszerviz-nyari-felkeszules.webp";
import hosszuAutoutImg from "@/assets/blog/nyari-hosszu-autout-felkeszules.webp";
import teliGumiImg from "@/assets/blog/teli-gumi-csere-gumiszereles-eger-2026.webp";
import emeloImg from "@/assets/blog/lakrosz-autoszerviz-uj-negyoszlopos-emelo-eger.webp";

export type BlogCategory =
  | "Szerviz tippek"
  | "Használt autó"
  | "Karbantartás"
  | "Közlekedés"
  | "Autós tudnivalók";

export type BlogBlock =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "table"; title?: string; rows: [string, string][] }
  | { type: "callout"; title?: string; text: string }
  | { type: "cta"; label: string; href: string; secondaryLabel?: string; secondaryHref?: string };

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  category: BlogCategory;
  date: string; // ISO
  readMinutes: number;
  image: string;
  imageAlt: string;
  imageCaption?: string;
  seo: {
    metaTitle: string;
    metaDescription: string;
  };
  content: BlogBlock[];
  featured?: boolean;
}

/**
 * Új cikk hozzáadása:
 * 1. Adj hozzá egy új objektumot ehhez a tömbhöz a többi mintájára.
 * 2. A `slug` egyedi és URL-barát legyen (ékezet nélkül, kötőjelekkel).
 * 3. A `date` ISO formátum (YYYY-MM-DD).
 * 4. Új képet importálj a fájl tetején a `src/assets/blog/` mappából.
 */
export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "mikor-cserejunk-teli-gumira-2026",
    title: "Mikor cseréljünk téli gumira 2026-ban? – Gumiszerelés Egerben",
    excerpt:
      "A 7 °C-os szabály, a futófelület mélysége, a DOT-kód és a helyes tárolás: összeszedtük, mikor és miért érdemes téli gumira váltani 2026 őszén Egerben és környékén.",
    category: "Szerviz tippek",
    date: "2026-10-14",
    readMinutes: 5,
    image: teliGumiImg,
    imageAlt:
      "Egymásra rakott téli gumik alufelnivel egy egri autószerviz műhelyében, a nyitott kapun át deres őszi táj",
    imageCaption:
      "A téli gumi akkor ad biztonságot, ha időben, jó állapotban és megfelelő mélységű mintázattal kerül fel az autóra.",
    seo: {
      metaTitle: "Mikor cseréljünk téli gumira 2026-ban? | LAKROSZ Eger",
      metaDescription:
        "7 °C szabály, profilmélység, DOT-kód és tárolás: így tudod, mikor kell téli gumira váltani. Gumiszerelés Egerben, időpont: +36 30 478 7619.",
    },
    featured: true,
    content: [
      {
        type: "p",
        text: 'Minden ősszel ugyanaz a kérdés: most kell már téli gumira váltani, vagy ráér még? Erre nincs egyetlen naptári dátum, de van néhány egyszerű szabály, amellyel könnyen eldöntheted. Ebben a cikkben végigmegyünk a legfontosabb szempontokon, hogy 2026 őszén is biztonságosan, és sorban állás nélkül kerüljön fel a téli abroncs az autódra. Ha már tudod, hogy cserélni szeretnél, a <a href="/gumiszereles-eger">gumiszerelés Egerben</a> oldalon minden részletet megtalálsz.',
      },
      { type: "h2", text: "A 7 °C-os szabály: mikor jön el a váltás ideje?" },
      {
        type: "p",
        text: "A nyári gumi keverékét meleg időre tervezték. Nagyjából 7 °C alatt a gumi megkeményedik, a tapadása érezhetően romlik, és a fékút hosszabb lesz — akkor is, ha az út száraz. A téli abroncs lágyabb keveréke és sűrű lamellázása épp ebben a tartományban kezd jobban teljesíteni.",
      },
      {
        type: "p",
        text: "A gyakorlati szabály: ha a reggeli és esti hőmérséklet tartósan 7 °C alá esik, itt az ideje a cserének. Egerben és a Bükk lábánál ez jellemzően október közepétől november elejéig következik be, a magasabban fekvő településeken (például Felsőtárkány vagy Noszvaj felé) gyakran korábban. Ne az első hóesést várd: a hideg, nedves, avaros aszfalt már jóval előtte veszélyes lehet.",
      },
      { type: "h2", text: "Futófelület-mélység: mennyi az elég?" },
      {
        type: "p",
        text: "Magyarországon a törvényes minimum 1,6 mm, de téli gumi esetén ez kevés. A szakmai ajánlás legalább 4 mm: ez alatt a mintázat már nem tudja hatékonyan kivezetni a latyakot és a vizet, a hóban pedig gyorsan elveszíti a tapadását.",
      },
      {
        type: "ul",
        items: [
          "4 mm felett: a téli gumi még jól teljesít.",
          "3–4 mm között: ez lehet az utolsó szezonja, érdemes figyelni.",
          "3 mm alatt: téli használatra már nem ajánlott, cserélni kell.",
          "Egyenetlen kopás: futóműhibára vagy rossz guminyomásra utalhat.",
        ],
      },
      {
        type: "p",
        text: 'Ha a gumi egyik oldalon jobban kopik, az gyakran beállítási probléma. Ilyenkor érdemes a cserét egy <a href="/futomu-javitas-eger">futómű ellenőrzéssel</a> összekötni, különben az új gumi is gyorsan tönkremegy.',
      },
      { type: "h2", text: "DOT-kód és a gumi kora" },
      {
        type: "p",
        text: "A gumi oldalfalán található DOT-kód utolsó négy számjegye a gyártás hetét és évét mutatja. Például a „4221” azt jelenti, hogy az abroncs 2021 42. hetében készült. A gumi akkor is öregszik, ha keveset használod: a keverék kiszárad, megkeményedik, és apró repedések jelenhetnek meg rajta.",
      },
      {
        type: "p",
        text: "Általános ökölszabály, hogy a 6 évnél idősebb téli gumit akkor is érdemes alaposan megvizsgáltatni, ha a mintázata még elegendőnek tűnik, 8–10 év felett pedig már cserélni ajánlott. Csere előtt a gumiszerelés során ezt is ellenőrizzük, és szólunk, ha valamelyik abroncs már nem biztonságos.",
      },
      { type: "h2", text: "A nyári gumi helyes tárolása" },
      {
        type: "p",
        text: "A leszerelt nyári abroncs élettartama nagyban múlik azon, hogyan tárolod. Néhány egyszerű szabály sokat segít:",
      },
      {
        type: "ul",
        items: [
          "Hűvös, száraz, sötét helyen tárold, közvetlen napfénytől és hőforrástól távol.",
          "Felnis gumit fektetve egymásra vagy felakasztva, felni nélkülit állítva tárolj, és havonta forgasd meg.",
          "Tartsd távol oldószertől, olajtól és üzemanyagtól.",
          "Tárolás előtt mosd le, és jelöld meg, melyik kerékpozícióról került le.",
        ],
      },
      { type: "h2", text: "Mikor érdemes időpontot kérni?" },
      {
        type: "p",
        text: "A legnagyobb roham általában az első hideg reggelek után indul, ilyenkor sok szervizben napokat kell várni. A legjobb, ha már október elején, a hidegfront előtt egyeztetsz időpontot: így nem kell kapkodni, és a csere mellé jut idő centrírozásra, szelepcserére és a guminyomás pontos beállítására is.",
      },
      {
        type: "p",
        text: 'Ha a cserével egy időben az autó téli felkészítését is elvégeznéd — fékek, akkumulátor, fagyálló —, nézd meg az <a href="/autoszerviz-eger">autószerviz Eger</a> és a <a href="/fekjavitas-eger">fékjavítás</a> oldalainkat is.',
      },
      {
        type: "table",
        title: "Ellenőrzőlista téli gumicsere előtt",
        rows: [
          ["Hőmérséklet", "Tartósan 7 °C alatt? Akkor váltani kell."],
          ["Profilmélység", "Legalább 4 mm a téli gumin."],
          ["DOT-kód", "6 év felett vizsgáltasd, 8–10 év felett cseréld."],
          ["Oldalfal", "Nincs repedés, dudor vagy sérülés."],
          ["Kopáskép", "Egyenletes? Ha nem, futómű-ellenőrzés kell."],
          ["Tárolás", "A nyári gumi tisztán, jelölve, hűvös helyre kerül."],
          ["Időpont", "Egyeztess még a nagy roham előtt: +36 30 478 7619."],
        ],
      },
      {
        type: "cta",
        label: "Időpontot kérek",
        href: "/#contact",
        secondaryLabel: "Gumiszerelés Egerben",
        secondaryHref: "/gumiszereles-eger",
      },
    ],
  },
  {
    slug: "uj-negyoszlopos-emelo-autoszerviz-eger",
    title: "Új, 5 tonnás négyoszlopos emelővel fejlődött a LAKROSZ Autószerviz",
    excerpt:
      "Új, elektrohidraulikus négyoszlopos emelő állt munkába az egri műhelyünkben: 5 tonnás teherbírás, 2,5 tonnás aknaperem-emelő, hosszabb kishaszonjárművek fogadása. Összeszedtük, mit jelent ez a gyakorlatban az autósoknak.",
    category: "Szerviz tippek",
    date: "2026-08-04",
    readMinutes: 6,
    image: emeloImg,
    imageAlt:
      "A LAKROSZ Autószerviz új, 5 tonnás négyoszlopos emelője Egerben, rajta egy fehér kishaszonjárművel",
    imageCaption:
      "Az új, 5 tonnás négyoszlopos emelő személyautók és nagyobb kishaszonjárművek biztonságos emelését is lehetővé teszi.",
    seo: {
      metaTitle: "Új 5 tonnás emelő – autószerviz Eger | LAKROSZ",
      metaDescription:
        "Új, 5 tonnás négyoszlopos emelő érkezett a LAKROSZ Autószerviz egri műhelyébe. Mutatjuk, mit jelent ez az autósok és a nagyobb járművek szervizelésénél.",
    },
    featured: true,
    content: [
      {
        type: "p",
        text: 'A műhelyünk technikai felszereltsége újabb komoly lépéssel bővült: munkába állt egy elektrohidraulikus, négyoszlopos emelő, amelyen már a hosszabb és nehezebb kishaszonjárművek is elférnek. Ha eddig is <a href="/autoszerviz-eger">autószerviz Egerben</a> volt a keresésed, most annyi a különbség, hogy egy stabilabb, nagyobb teherbírású munkaállomáson tudjuk átnézni és javítani az autódat — legyen szó egy kisautóról, egy SUV-ról vagy egy megpakolt furgonról.',
      },
      {
        type: "p",
        text: "Az emelő nem önmagában érdekes. Az számít, hogy mit tudunk vele jobban megcsinálni: alaposabban hozzáférünk a jármű alsó részéhez, tehermentesített kerekek mellett vizsgálhatjuk a futóművet, és nem kell a kocsit többször átszerelni egyik állásból a másikba egy összetettebb munka közben.",
      },
      { type: "h2", text: "Milyen emelő érkezett a LAKROSZ Autószervizbe?" },
      {
        type: "p",
        text: "A berendezés egy Lincos STD-5650 típusú, elektrohidraulikus négyoszlopos emelő. A fő emelő teherbírása 5 tonna, ehhez tartozik egy 2,5 tonnás elektrohidraulikus aknaperem-emelő, amellyel a járművet a saját emelési pontjainál is meg lehet emelni. A két egység között hidraulikus váltószeleppel lehet váltani, a túlemelést pedig végálláskapcsoló segít megakadályozni.",
      },
      {
        type: "p",
        text: "A négyoszlopos kialakítás lényege, hogy a jármű a saját kerekein, két hosszú rámpán áll — ez adja a nagy stabilitást és a magas terhelhetőséget. Az emelő csúszólappal van szerelve, és a konstrukciója alkalmas futóműállító rendszer későbbi beépítésére is. Fontos: futóműállító berendezés jelenleg nincs beépítve, az emelő ezt csak lehetővé teszi a jövőben.",
      },
      {
        type: "table",
        title: "Műszaki adatok",
        rows: [
          ["Típus", "Lincos STD-5650, elektrohidraulikus négyoszlopos emelő"],
          ["Fő emelő teherbírása", "5 tonna"],
          ["Aknaperem-emelő teherbírása", "2,5 tonna"],
          ["Maximális emelési magasság", "1700 mm"],
          ["Emelési idő", "kb. 60 másodperc"],
          ["Áramellátás", "400 V"],
          ["Motorteljesítmény", "2,2 kW"],
          ["Rámpák", "2 db felhajtórámpa"],
          ["Rámpa hossza / szélessége", "5000 mm / 580 mm"],
          ["Rámpák közötti távolság", "880 vagy 960 mm"],
          ["Teljes hosszúság / szélesség", "5825 mm / 3286 mm"],
          ["Biztonság", "végálláskapcsoló, hidraulikus váltószelep"],
          ["Egyéb", "csúszólap, futóműállító rendszer beépítésére alkalmas"],
        ],
      },
      { type: "h2", text: "Miért fontos az 5 tonnás teherbírás?" },
      {
        type: "p",
        text: "Egy átlagos személyautó 1,2–1,8 tonna, egy nagyobb SUV vagy egy hosszabb kishaszonjármű viszont megrakva könnyen 2,5–3,5 tonna is lehet. Az 5 tonnás teherbírás nem azért fontos, hogy kihasználjuk a felső határt, hanem azért, mert bőséges tartalékkal dolgozunk: a szerkezet a mindennapi terhelésnél messze a határa alatt marad, ami stabilabb, nyugodtabb munkát jelent.",
      },
      {
        type: "p",
        text: "Az 5 méteres rámpahossz emellett azt is megoldja, hogy a hosszabb furgonok, kisteherautók is teljes egészében felférjenek. Ez a gyakorlatban azt jelenti, hogy a vállalkozói flottákat, szervizautókat, kisbuszokat ugyanúgy tudjuk fogadni, mint egy családi kombit.",
      },
      { type: "h2", text: "Hogyan segíti az emelő a futómű- és fékjavítást?" },
      {
        type: "p",
        text: "A négyoszlopos emelőn a jármű a kerekein áll, tehát terhelt állapotban is vizsgálható — így jól látszik az alváz, a kipufogórendszer, a fékvezetékek és a futómű valós, menet közbeni helyzete. Amikor viszont a kerekeket tehermentesíteni kell, jön képbe az aknaperem-emelő: ezzel a járművet a gyári emelési pontjainál emeljük meg, és a kerekek szabaddá válnak.",
      },
      {
        type: "ul",
        items: [
          "futóműalkatrészek – lengőkarok, szilentek, gömbfejek, stabilizátor-összekötők holtjátékának vizsgálata",
          "fékrendszer – féktárcsa, fékbetét, féknyereg, fékvezeték ellenőrzése és cseréje",
          "kerékcsapágyak – zajra, játékra történő ellenőrzés forgatható keréknél",
          "alvázvizsgálat – korrózió, sérülés, alsó burkolatok állapota",
          "kipufogórendszer – kötések, felfüggesztések, tömítetlenség keresése",
        ],
      },
      {
        type: "p",
        text: 'Ezért kapcsolódik szorosan az új emelő a <a href="/futomu-javitas-eger">futómű javítás Egerben</a> és a <a href="/fekjavitas-eger">fékjavítás Egerben</a> típusú munkákhoz: pontosan azokhoz a feladatokhoz ad jobb hozzáférést, ahol a hiba megtalálása legalább annyi időt vesz igénybe, mint maga a csere.',
      },
      { type: "h2", text: "Mit jelent az új emelő a LAKROSZ műhelyének?" },
      {
        type: "p",
        text: "Elsősorban jobb munkaszervezést. Egy összetettebb javításnál eddig gyakran mozgatni kellett az autót a különböző munkafázisok között; most több kapcsolódó feladat elvégezhető egy állásban, felesleges átszerelés nélkül. Ez nem varázsütésre gyorsabb javítást jelent, hanem kiszámíthatóbb, rendezettebb munkafolyamatot.",
      },
      {
        type: "p",
        text: "Másodsorban szélesebb járműkört: a személyautók mellett a nagyobb kishaszonjárművek is beférnek a napi menetrendbe. Hosszú távon pedig fejlesztési irány is, hiszen az emelő felkészített a futóműállító rendszer későbbi beépítésére. Az ilyen berendezések telepítését, üzembe helyezését, időszakos vizsgálatát és üzemeltetését egyébként a vonatkozó emelőgép-biztonsági előírások szerint kell végezni, többek között a 47/1999. (VIII. 4.) GM rendelet figyelembevételével.",
      },
      { type: "h2", text: "Mit nyernek vele a LAKROSZ ügyfelei?" },
      {
        type: "ul",
        items: [
          "nagyobb és nehezebb járművek fogadása – furgon, kisbusz, megrakott haszongépjármű",
          "alaposabb, biztonságosabb hozzáférés a jármű alsó részeihez",
          "pontosabb állapotfelmérés terhelt és tehermentesített helyzetben egyaránt",
          "több kapcsolódó javítás elvégezhető egy munkaállásban",
          "átláthatóbb visszajelzés arról, melyik alkatrész kopott vagy hibás",
        ],
      },
      {
        type: "callout",
        title: "Megmutatjuk, mit látunk",
        text: 'Ha szeretnéd, az emelőn állva megmutatjuk, pontosan melyik alkatrésznél tapasztaltunk kopást vagy játékot. Így nem kell elhinned, hogy mit írunk a munkalapra — látod is. Ha érdekelnek <a href="/blog">további autós tippek és szervizhírek</a>, a blogunkon rendszeresen írunk gyakorlati témákról.',
      },
      { type: "h2", text: "Futómű- vagy általános autójavítás Egerben?" },
      {
        type: "p",
        text: 'Ha futóműhibát, fékproblémát, rendellenes zajt vagy más műszaki hibát tapasztalsz, kérj időpontot a LAKROSZ Autószerviz egri műhelyébe. Az új emelő segítségével személyautók és nagyobb kishaszonjárművek átvizsgálását és javítását is vállaljuk. Telefon: +36 30 478 7619, cím: 3300 Eger, Vécseyvölgy utca 99. Írhatsz is nekünk a <a href="/#contact">kapcsolat szekcióban</a>.',
      },
      { type: "h2", text: "Gyakori kérdések az új emelővel kapcsolatban" },
      { type: "h3", text: "Milyen járműveket lehet az új emelővel megemelni?" },
      {
        type: "p",
        text: "Személyautókat, SUV-kat, valamint hosszabb és nehezebb kishaszonjárműveket 5 tonna össztömegig, legfeljebb 1700 mm emelési magasságig.",
      },
      { type: "h3", text: "Mire használható az aknaperem-emelő?" },
      {
        type: "p",
        text: "A 2,5 tonnás elektrohidraulikus segédemelővel a jármű a saját emelési pontjainál emelhető meg, így a kerekek tehermentesített állapotba kerülnek — ez kell a futómű-, fék- és kerék körüli munkákhoz.",
      },
      { type: "h3", text: "Alkalmas az emelő futóműjavításra?" },
      {
        type: "p",
        text: "Igen, a futóműalkatrészek vizsgálatához és cseréjéhez alkalmas. Futóműállító berendezés jelenleg nincs beépítve; az emelő konstrukciója viszont alkalmas ilyen rendszer későbbi beépítésére.",
      },
      { type: "h3", text: "Hol található a LAKROSZ Autószerviz?" },
      {
        type: "p",
        text: "3300 Eger, Vécseyvölgy utca 99. Időpontot a +36 30 478 7619-es telefonszámon vagy a weboldal kapcsolat szekciójában tudsz kérni.",
      },
      {
        type: "p",
        text: 'Forrás a műszaki adatokhoz: <a href="https://szerszampiac.hu/hirdetes/lincos-negyoszlopos-emelo-5t-teherbirassal-aknaperem-emelovel-std-5650-2536770-2536770" target="_blank" rel="noopener noreferrer">a berendezés gyártói/forgalmazói adatlapja</a>.',
      },
    ],
  },
  {
    slug: "nyari-guminyomas-es-abroncs-hosegben",
    title: "Nyári hőség és a gumiabroncs: így kerüld el a defektet 35 fokban",
    excerpt:
      "Egy 35 fokos aszfalton a gumi belső nyomása és hőmérséklete jelentősen megnő. A nyári hónapokban az abroncs állapota és a guminyomás többet számít, mint bármikor máskor — egy elhanyagolt kerék autópályán könnyen szétmegy.",
    category: "Karbantartás",
    date: "2026-06-18",
    readMinutes: 6,
    image: guminyomasImg,
    imageAlt: "Guminyomásmérő egy autó kerekén nyári forró aszfalton",
    seo: {
      metaTitle: "Nyári guminyomás és abroncs hőségben | LAKROSZ Autószerviz",
      metaDescription:
        "Mire figyelj a nyári gumi és guminyomás körül melegben: ellenőrzés, terhelés, autópálya, defekt megelőzése. Tippek az egri LAKROSZ Autószerviztől.",
    },
    featured: true,
    content: [
      {
        type: "p",
        text: "Nyáron az autósok szinte minden veszélyforrásra gondolnak — a klímára, a motormelegedésre, a hosszú útra — csak éppen a gumira nem. Pedig a forró aszfalt és a magas külső hőmérséklet a leginkább a kerekeket terheli: 35 fokos napon egy fekete aszfalton a gumi felszíni hőmérséklete bőven 60 fok fölé mehet, és ez a hőterhelés egy elöregedett vagy alulfújt abroncson percek alatt katasztrófához vezethet.",
      },
      { type: "h2", text: "Miért veszélyesebb nyáron a guminyomás?" },
      {
        type: "p",
        text: "A levegő tágul, ha melegszik. Egy hideg garázsban beállított nyomás autópályán, hosszabb gyors haladás után könnyen 0,3–0,5 bar-ral is nő. Ez önmagában még belefér — a baj akkor van, ha hideg állapotban már eleve alulfújt a gumi: ekkor az oldalfal túlságosan deformálódik, túlmelegszik, és a belső szerkezet (kordszálak, gumiréteg) elválik egymástól. Innen percek kérdése az úgynevezett szétpörgés vagy a defekt.",
      },
      { type: "h2", text: "Mit ellenőrizz nyáron havonta?" },
      {
        type: "ul",
        items: [
          "guminyomás hideg állapotban — minden kerékben, beleértve a pótkereket is",
          "bordázatmélység: legalább 3 mm a biztonságos vízelvezetéshez nyári záporban",
          "oldalfal állapota: repedések, dudorok, beékelődött kavicsok",
          "egyenetlen kopás — futómű- vagy nyomáshibára utal",
          "az abroncs DOT-száma: 6-7 év fölötti gumi már öreg, akkor is, ha jól néz ki",
        ],
      },
      { type: "h3", text: "Pakolt autó? Növeld a nyomást!" },
      {
        type: "p",
        text: "Hosszú nyaralásra induló, pakolt autónál a gyári táblázat szinte mindig külön, magasabb értéket ad meg „teljes terhelés” esetére. Ezt a tankajtó belsején vagy a vezetőajtó B-oszlopán találod. Ha négy felnőtt, a teljes csomagtér és tetőcsomagtartó is kerül a kocsira, a normál városi nyomás nem elég — a túl puha kerék autópályán súlyos hibákat okozhat.",
      },
      { type: "h2", text: "Mi történik egy nagy sebességű defektnél?" },
      {
        type: "p",
        text: "130 km/h-nál egy hirtelen kipukkanó gumi az autó stabilitását teljesen felboríthatja. Aki nem készült rá, ösztönösen rántja a kormányt, ami további megcsúszáshoz vezet. A jó hír: a defektek 80%-a megelőzhető a guminyomás és az abroncs állapotának rendszeres ellenőrzésével — ez egyetlen szerviz előtti kávészünet alatt elvégezhető.",
      },
      {
        type: "callout",
        title: "Tipp",
        text: "Ha hosszú útra mész, a guminyomást indulás előtti reggelen, hideg gumin ellenőrizd — ne a benzinkúton, miután 20 km-t mentél. A meleg kerékben mért érték torzít, és könnyen alulfújva indulsz el.",
      },
      { type: "h2", text: "Mikor cseréld a nyári gumit?" },
      {
        type: "p",
        text: "Egy 5–6 évnél idősebb gumi a bordázat mélységétől függetlenül elöregedhet. A gumikeverék kiszárad, repedezik, az oldalfal merevvé és törékennyé válik. Nyáron, hőterhelés alatt egy ilyen abroncs viselkedése kiszámíthatatlan. A DOT-szám az oldalfalon megmutatja a gyártási hetet és évet — ha bizonytalan vagy, hozd be, megnézzük.",
      },
      { type: "h2", text: "Hogyan segítünk a LAKROSZ Autószervizben?" },
      {
        type: "p",
        text: 'Egerben, a Vécseyvölgy utca 99. alatti műhelyünkben <a href="/gumiszereles-eger">gumiszerelést, centrírozást</a> és teljes abroncs-átvizsgálást is végzünk. Ha a gumikopás egyenetlen, a futóművet is átnézzük — gyakran egy <a href="/futomu-javitas-eger">futómű-beállítás</a> oldja meg a problémát, nem új gumi. A komplett nyári biztonsági átnézést pedig az <a href="/autoszerviz-eger">autószerviz</a> szolgáltatásunk részeként szoktuk elvégezni.',
      },
    ],
  },

  {
    slug: "klimaszerviz-nyari-utazas-elott",
    title: "Nem hűt a klíma? Erre figyelj a nyári út előtt",
    excerpt:
      "A 35 fokos kánikulában a klíma nem luxus, hanem biztonsági kérdés — egy túlfűtött utastérben a vezető reakcióideje érezhetően romlik. Mutatjuk, mit jelent a klímaszerviz, mikor kell, és miért nem érdemes a nyár közepén várni vele.",
    category: "Szerviz tippek",
    date: "2026-06-11",
    readMinutes: 6,
    image: klimaSzervizImg,
    imageAlt: "Autóklíma szerviz gép R1234yf hűtőközeggel csatlakoztatva egy autóra a műhelyben",
    seo: {
      metaTitle: "Klímaszerviz nyári utazás előtt | LAKROSZ Autószerviz Eger",
      metaDescription:
        "Mikor és miért kell autóklíma szerviz, hogyan veszít gázt a rendszer, mit ér a pollenszűrő csere. Klímaszerviz és nyári átvizsgálás Egerben a LAKROSZ-nál.",
    },
    featured: true,
    content: [
      {
        type: "p",
        text: "A legtöbb autós csak akkor jut eszébe a klíma, amikor már nem hűt rendesen. Pedig a klímarendszer évről évre veszít a hatékonyságából: a hűtőközeg lassan, természetes módon szivárog a rendszerből, a párologtató bepiszkolódik, a pollenszűrő telítődik. Egy átlagos autón 2–3 évente érdemes klímaszervizt csinálni — különösen, ha nyáron sokat és hosszan használod.",
      },
      { type: "h2", text: "Miért nem hűt jól a klíma?" },
      {
        type: "ul",
        items: [
          "kevés a rendszerben a hűtőközeg — szivárgás vagy természetes párolgás miatt",
          "eltömődött pollenszűrő — kevés a befújt levegő, kellemetlen szag jelenik meg",
          "piszkos párologtató — gomba- és baktériumlerakódás, „dohos” utastér",
          "hibás kompresszortengelykapcsoló vagy nyomáskapcsoló",
          "elöregedett klímaszárító, ami már nem tudja eltávolítani a nedvességet",
        ],
      },
      { type: "h2", text: "Mit jelent egy teljes klímaszerviz?" },
      { type: "h3", text: "1. Nyomásteszt és rendszerellenőrzés" },
      {
        type: "p",
        text: "Először megnézzük, mennyi gáz van a rendszerben, és van-e szivárgás. Egy modern klímaszerviz gép szivárgásteszttel képes kimutatni a leggyakoribb meghibásodásokat — szelepek, tömlők, csatlakozók, kondenzátor. Hiba esetén nem érdemes csak újratölteni, mert egy hónap múlva ugyanott vagyunk.",
      },
      { type: "h3", text: "2. Vákuumozás és újratöltés" },
      {
        type: "p",
        text: "A régi hűtőközeget és olajat lemérve leszívjuk, a rendszert vákuum alá helyezzük (ez kiszárítja a nedvességet és kiszívja a maradék levegőt), majd a gyári előírás szerinti mennyiségű hűtőközeggel és olajjal töltjük újra. Egy szakszerűen feltöltött klíma érezhetően hűt — már 1-2 perc alatt hideg levegő érkezik.",
      },
      { type: "h3", text: "3. Pollenszűrő csere és tisztítás" },
      {
        type: "p",
        text: "A pollenszűrő minden évszakváltáskor cserére szorul, de évente egyszer mindenképp. Egy eltömődött szűrő a klíma teljesítményét érdemben rontja, és pollenszezonban allergiás panaszokat is okoz. A párologtató fertőtlenítő tisztítás során a baktérium- és gombalerakódásokat is eltávolítjuk, megszüntetve a kellemetlen szagokat.",
      },
      { type: "h2", text: "Miért nem érdemes a kánikula közepén várni?" },
      {
        type: "p",
        text: "Nyár közepén minden szerviznél torlódik a klímamunka. Egy júniusi vagy júliusi időpontot sokszor csak 1-2 hetes várakozással lehet kapni — és a hűtőközeg ára is folyamatosan emelkedik. A legjobb stratégia: május végén, kora júniusban bevinni az autót, mielőtt jön az igazi hőség.",
      },
      {
        type: "callout",
        title: "Biztonsági kérdés",
        text: "Egy 40 fokos utastérben a vezető reakcióideje akár 20-30%-kal is romolhat — ez fáradtsággal és koncentrációesésssel jár. Egy működő klíma nem komfort, hanem aktív biztonsági elem, különösen hosszú nyári utakon, gyerekkel az autóban.",
      },
      { type: "h2", text: "Klímaszerviz Egerben — LAKROSZ" },
      {
        type: "p",
        text: 'A LAKROSZ Autószervizben modern klímaszerviz géppel dolgozunk, R134a és R1234yf közeghez egyaránt. A klímaszervizt jellemzően egy nyári átfogó átvizsgálás keretében végezzük — ennek részeként a <a href="/autoszerviz-eger">teljes autószerviz</a> során a hűtőrendszert, fékfolyadékot és a futóművet is átnézzük. Ha hosszú útra készülsz, érdemes egyben elvégezni mindent — a nyári indulás előtt sokan a <a href="/gumiszereles-eger">gumiszerelést és centrírozást</a> is ilyenkor intézik el nálunk, hogy egy alkalommal minden készen legyen.',
      },
    ],
  },

  {
    slug: "hosszu-nyari-autout-felkeszules",
    title: "Hosszú nyári autóútra készülsz? Ezt nézd át, mielőtt elindulsz",
    excerpt:
      "Egy 800-1500 kilométeres horvát, olasz vagy görög nyaralás előtt egészen más szempontokat kell végigvenni az autón, mint egy hétköznapi városi használatnál. Összegyűjtöttük, mit nézz át — vagy nézessünk át veled — indulás előtt.",
    category: "Közlekedés",
    date: "2026-06-04",
    readMinutes: 7,
    image: hosszuAutoutImg,
    imageAlt: "Csomagokkal megpakolt autó csomagtartó nyitott ajtaja egy nyári országúton",
    seo: {
      metaTitle: "Nyári hosszú autóút – felkészülési checklist | LAKROSZ",
      metaDescription:
        "Mit nézz át hosszú nyári út előtt: olaj, hűtés, fék, gumi, klíma, akkumulátor. Komplett indulás előtti átvizsgálás Egerben a LAKROSZ Autószervizben.",
    },
    content: [
      {
        type: "p",
        text: "Egy hosszú nyári autóút — Horvátország, Olaszország, Görögország, vagy akár egy másfél hetes magyarországi körutazás — teljesen más megterhelést jelent az autónak, mint a napi 15-20 km-es városi taxizás. Magas külső hőmérséklet, tartós autópályás sebesség, teljes terhelés (négy fő plusz csomagok plusz esetleg tetőcsomagtartó) és sokszor 8-10 órás folyamatos vezetés. Ezt nem szabad megtervezetlenül belevágni.",
      },
      { type: "h2", text: "Az indulás előtti átvizsgálás 7 pontja" },
      { type: "h3", text: "1. Motorolaj és olajszint" },
      {
        type: "p",
        text: "Tartós autópályás haladásnál a motor sokkal többet és melegebben dolgozik. Ha közeleg az olajcsere, ne hagyd a túloldalra — végezd el indulás előtt. Az olajszintet és az olaj állapotát hideg motoron, vízszintes felületen érdemes ellenőrizni.",
      },
      { type: "h3", text: "2. Hűtőrendszer" },
      {
        type: "p",
        text: "A hűtőfolyadék szintje, a hűtőborda tisztasága, a ventilátor működése mind kritikus 35 fokos olasz autópályán. Egy elöregedett hűtőfolyadék vagy egy alig szivárgó tömlő otthon nem feltűnő — egy 800 km-es úton viszont könnyen leállíthatja az autót.",
      },
      { type: "h3", text: "3. Fékek" },
      {
        type: "p",
        text: 'A hegyvidéki lejtőkön (pl. olasz, szlovén vagy ausztriai szakaszok) a fék komoly terhelést kap. Vékony fékbetét vagy elöregedett fékfolyadék itt mutatja meg magát — a fékfolyadék forráspontja nedvességgel telítve drasztikusan csökken. Indulás előtt érdemes egy <a href="/fekjavitas-eger">fékrendszer-átvizsgálást</a> kérni, ha 1-2 éve nem volt. A hosszú út előtti komplett átnézést az <a href="/autoszerviz-eger">autószerviz</a> szolgáltatásunk keretében végezzük, az abroncsokat pedig <a href="/gumiszereles-eger">gumiszerelés és centrírozás</a> során ellenőrizzük.',
      },
      { type: "h3", text: "4. Gumiabroncsok" },
      {
        type: "ul",
        items: [
          "guminyomás terhelés szerinti, magasabb értéken (tankajtó tábla)",
          "bordázatmélység minimum 3 mm",
          "oldalfal repedés- és dudormentes",
          "pótkerék (vagy defektjavító készlet) a helyén és használható állapotban",
        ],
      },
      { type: "h3", text: "5. Klíma" },
      {
        type: "p",
        text: "Egy 10 órás úton egy gyengén hűtő klíma nem csupán kényelmetlen — a fáradtság és a kiszáradás vezetéstechnikai hibákhoz vezet. Ha a klíma érezhetően gyengébben hűt, mint tavaly, mindenképp érdemes egy klímaszervizt csinálni indulás előtt.",
      },
      { type: "h3", text: "6. Akkumulátor és elektromos rendszer" },
      {
        type: "p",
        text: "Sokan azt hiszik, az akkumulátor csak télen áll le — pedig a nyári hőség is komolyan terheli. A klíma, a fűthető-hűthető ülések, a hűtős poharak mind extra fogyasztók. Egy 5 évnél idősebb akkumulátort érdemes ellenőriztetni.",
      },
      { type: "h3", text: "7. Világítás, ablakmosó, ablaktörlő" },
      {
        type: "p",
        text: "Nyári záporban másodpercek alatt kell minden látható. Repedt vagy hibás ablaktörlő, kiégett féklámpa, üres szélvédőmosó tartály — apróságnak tűnnek, de balesetveszélyesek. Mindegyiket pótold otthon, mielőtt elindulsz.",
      },
      {
        type: "callout",
        title: "Csomagolás autószempontból",
        text: "A csomagsúlyt egyenletesen oszd el, a nehezét lent és középen helyezd el. A tetőcsomagtartó komolyan ront a fogyasztáson és a kezelhetőségen — ha nem feltétlenül szükséges, hagyd otthon. A pakolt autó fékútja érezhetően hosszabb, ezt érdemes az első 100 km-en megszokni.",
      },
      { type: "h2", text: "Külföldi úton ezeket vidd magaddal" },
      {
        type: "ul",
        items: [
          "elakadásjelző háromszög és láthatósági mellény (minden utasnak külföldön sok helyen kötelező)",
          "elsősegélydoboz és lejárati dátum-ellenőrzés",
          "zöldkártya és érvényes biztosítás",
          "tartalék izzókészlet és biztosítékok (egyes országokban előírás)",
          "alkoholszonda (pl. Franciaország)",
          "töltőkábel, jó minőségű autós telefontöltő, navigáció offline térképpel",
        ],
      },
      { type: "h2", text: "Indulás előtti komplett átvizsgálás Egerben" },
      {
        type: "p",
        text: 'A LAKROSZ Autószervizben egy 1-1,5 órás indulás előtti átvizsgálás során az összes fenti pontot átnézzük, és ha találunk valamit, közösen eldöntjük, mit kell most javítani, és mi az, ami még nyugodtan elmehet. A célunk, hogy a nyaralásod ne a sztráda mellett egy autómentő várásával kezdődjön. Időpontot a <a href="/#contact">kapcsolat oldalon</a> tudsz kérni, vagy hívd a +36 30 478 7619-es számot.',
      },
    ],
  },
];

export function getPostBySlug(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((p) => p.slug === slug);
}

export function getFeaturedPosts(limit = 3): BlogPost[] {
  const featured = BLOG_POSTS.filter((p) => p.featured);
  const rest = BLOG_POSTS.filter((p) => !p.featured);
  return [...featured, ...rest].slice(0, limit);
}

export function getAllCategories(): BlogCategory[] {
  const set = new Set<BlogCategory>();
  BLOG_POSTS.forEach((p) => set.add(p.category));
  return Array.from(set);
}

export function formatHuDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("hu-HU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
